import * as graph from '@adc/sdk'

// Status color mapping for evaluation status
export const statusColorMapper = (
  status: graph.enums.CognitionEvaluationGroupStatus
): string => {
  switch (status) {
    case graph.enums.CognitionEvaluationGroupStatus.Completed:
      return 'success'
    case graph.enums.CognitionEvaluationGroupStatus.InProgress:
      return 'info'
    case graph.enums.CognitionEvaluationGroupStatus.Failed:
      return 'error'
    case graph.enums.CognitionEvaluationGroupStatus.Pending:
      return 'warning'
    default:
      return 'default'
  }
}

interface AggregationsInput {
  default_person_net_ranking_count?: Record<string, number>
  trusted_person_net_ranking_count?: Record<string, number>
  system_net_ranking_count?: Record<string, number>
  net_ranking_count?: Record<string, number>
  comment_count_count?: Record<string, number>
  [key: string]: unknown
}

interface ExtractMetricsResult {
  ranked: number
  rankedAndUnranked: number
  sumOfEndUserRankings: number
  sumOfTrustedUserRankings: number
  sumOfSystemRankings: number
  sumOfNetRankings: number
  upperBoundEndUserRanking: number
  upperBoundTrustedUserRanking: number
  upperBoundSystemRanking: number
  upperBoundNetRanking: number
  commented: number
  commentedAndUncommented: number
  netPassed: number
  netPassedAndFailed: number
  endUserPassed: number
  endUserPassedAndFailed: number
  trustedUserPassed: number
  trustedUserPassedAndFailed: number
  systemPassed: number
  systemPassedAndFailed: number
}

/**
 * Calculates the sum and upper bound for a ranking count object
 * @param rankingCount - Record of ranking values to their counts
 * @returns Object with sum and upperBound values
 */
const calculateRankingMetrics = (
  rankingCount: Record<string, number>
): { sum: number; upperBound: number } => {
  const sum = Object.entries(rankingCount).reduce((acc, [key, count]) => {
    const ranking = parseInt(key, 10)
    if (ranking !== 0) {
      return acc + ranking * count
    }
    return acc
  }, 0)

  const upperBound = Math.abs(sum)

  return { sum, upperBound }
}

/**
 * Calculates passed, failed, and total passed+failed counts for a ranking count object
 * @param rankingCount - Record of ranking values to their counts
 * @returns Object with passed, failed, and passedAndFailed values
 */
const calculatePassedFailedMetrics = (
  rankingCount: Record<string, number>
): { passed: number; failed: number; passedAndFailed: number } => {
  const passed = Object.entries(rankingCount).reduce((sum, [key, count]) => {
    const ranking = parseInt(key, 10)
    if (ranking > 0) {
      return sum + count
    }
    return sum
  }, 0)

  const failed = Object.entries(rankingCount).reduce((sum, [key, count]) => {
    const ranking = parseInt(key, 10)
    if (ranking < 0) {
      return sum + count
    }
    return sum
  }, 0)

  const passedAndFailed = passed + failed

  return { passed, failed, passedAndFailed }
}

/**
 * Extracts ranked and unranked metrics from aggregations data
 * @param aggregations - The aggregations object
 * @returns Object with ranked and rankedAndUnranked values
 */
export const extractMetrics = (
  aggregations: AggregationsInput
): ExtractMetricsResult => {
  // Handle undefined or null input
  const safeAggregations = aggregations || {}
  const netRankingCount: Record<string, number> =
    safeAggregations.net_ranking_count || {}
  const endUserRankingCount: Record<string, number> =
    safeAggregations.default_person_net_ranking_count || {}
  const trustedUserRankingCount: Record<string, number> =
    safeAggregations.trusted_person_net_ranking_count || {}
  const systemRankingCount: Record<string, number> =
    safeAggregations.system_net_ranking_count || {}

  // Calculate total sum of all values (ranked + unranked)
  const rankedAndUnranked = Object.values(netRankingCount).reduce(
    (sum, count) => sum + count,
    0
  )

  // Calculate ranked sum (excluding "0" key which represents unranked)
  const unrankedCount = netRankingCount['0'] || 0
  const ranked = rankedAndUnranked - unrankedCount

  // Calculate ranking metrics for all types
  const netRankingMetrics = calculateRankingMetrics(netRankingCount)
  const endUserRankingMetrics = calculateRankingMetrics(endUserRankingCount)
  const trustedUserRankingMetrics = calculateRankingMetrics(
    trustedUserRankingCount
  )
  const systemRankingMetrics = calculateRankingMetrics(systemRankingCount)

  // Calculate passed/failed metrics for all types
  const netPassedFailedMetrics = calculatePassedFailedMetrics(netRankingCount)
  const endUserPassedFailedMetrics =
    calculatePassedFailedMetrics(endUserRankingCount)
  const trustedUserPassedFailedMetrics = calculatePassedFailedMetrics(
    trustedUserRankingCount
  )
  const systemPassedFailedMetrics =
    calculatePassedFailedMetrics(systemRankingCount)

  /**
   * Commented...
   */
  const commentedCount = safeAggregations.comment_count_count || {}

  const commentedAndUncommented = Object.values(commentedCount).reduce(
    (sum, count) => sum + count,
    0
  )

  const commented = commentedAndUncommented - (commentedCount['0'] || 0)

  return {
    ranked,
    rankedAndUnranked,
    sumOfNetRankings: netRankingMetrics.sum,
    sumOfEndUserRankings: endUserRankingMetrics.sum,
    sumOfTrustedUserRankings: trustedUserRankingMetrics.sum,
    sumOfSystemRankings: systemRankingMetrics.sum,
    upperBoundNetRanking: netRankingMetrics.upperBound,
    upperBoundEndUserRanking: endUserRankingMetrics.upperBound,
    upperBoundTrustedUserRanking: trustedUserRankingMetrics.upperBound,
    upperBoundSystemRanking: systemRankingMetrics.upperBound,
    commented,
    commentedAndUncommented,
    netPassed: netPassedFailedMetrics.passed,
    netPassedAndFailed: netPassedFailedMetrics.passedAndFailed,
    endUserPassed: endUserPassedFailedMetrics.passed,
    endUserPassedAndFailed: endUserPassedFailedMetrics.passedAndFailed,
    trustedUserPassed: trustedUserPassedFailedMetrics.passed,
    trustedUserPassedAndFailed: trustedUserPassedFailedMetrics.passedAndFailed,
    systemPassed: systemPassedFailedMetrics.passed,
    systemPassedAndFailed: systemPassedFailedMetrics.passedAndFailed,
  }
}
